import { EMPTY_TODO, type LocalNote, type NoteKind, withDerived } from '@/domain/note';
import { toggleTaskLine } from '@/domain/markdown';
import type { TagnDB } from './db';

export type NotePatch = Partial<Pick<LocalNote, 'title' | 'content' | 'pinned' | 'archived' | 'kind'>>;

/** Local note mutations. Every change marks the note dirty for the next sync. */
export class NotesRepository {
  constructor(
    private readonly db: TagnDB,
    private readonly now: () => number = Date.now,
  ) {}

  async create(kind: NoteKind, ownerId: string, init: NotePatch = {}): Promise<LocalNote> {
    const now = this.now();
    const note: LocalNote = withDerived({
      id: crypto.randomUUID(),
      kind,
      title: '',
      content: kind === 'todo' ? EMPTY_TODO : '',
      pinned: false,
      archived: false,
      deleted: false,
      version: 0,
      createdAt: now,
      updatedAt: now,
      ownerId,
      role: 'owner' as const,
      shared: false,
      dirty: 1 as const,
      localRev: 1,
      ...init,
    });
    await this.db.notes.add(note);
    return note;
  }

  async get(id: string): Promise<LocalNote | undefined> {
    return this.db.notes.get(id);
  }

  /** Applies a patch. Returns false if nothing changed or the note is read-only. */
  async update(id: string, patch: NotePatch): Promise<boolean> {
    return this.db.transaction('rw', this.db.notes, async () => {
      const note = await this.db.notes.get(id);
      if (!note || note.role === 'viewer') return false;
      const changed = (Object.keys(patch) as (keyof NotePatch)[]).some((k) => patch[k] !== undefined && patch[k] !== note[k]);
      if (!changed) return false;
      const next = withDerived({ ...note, ...patch });
      await this.db.notes.put({ ...next, dirty: 1, localRev: note.localRev + 1, updatedAt: this.now() });
      return true;
    });
  }

  async toggleTask(id: string, lineIndex: number): Promise<boolean> {
    const note = await this.get(id);
    if (!note) return false;
    return this.update(id, { content: toggleTaskLine(note.content, lineIndex) });
  }

  /** Soft deletes a note (hard delete if it never reached the server). */
  async remove(id: string): Promise<void> {
    await this.db.transaction('rw', this.db.notes, async () => {
      const note = await this.db.notes.get(id);
      if (!note) return;
      if (note.version === 0) {
        await this.db.notes.delete(id);
        return;
      }
      await this.db.notes.put({ ...note, deleted: true, dirty: 1, localRev: note.localRev + 1, updatedAt: this.now() });
    });
  }

  /** Undoes a soft delete that has not been synced yet, or re-creates the note. */
  async restore(snapshot: LocalNote): Promise<void> {
    const current = await this.get(snapshot.id);
    if (current && !current.deleted) return;
    if (current?.dirty) {
      await this.db.notes.put({ ...snapshot, dirty: 1, localRev: current.localRev + 1 });
      return;
    }
    await this.db.notes.put({ ...snapshot, id: crypto.randomUUID(), version: 0, dirty: 1, deleted: false, role: 'owner', shared: false });
  }

  /** Copies a note (e.g. to keep a local version after a conflict). */
  async duplicate(note: LocalNote, ownerId: string, titleSuffix = ''): Promise<LocalNote> {
    return this.create(note.kind, ownerId, {
      title: note.title + titleSuffix,
      content: note.content,
      pinned: note.pinned,
      archived: note.archived,
    });
  }
}
